import { MailIcon, StarIcon } from '@heroicons/react/solid'

import { Dialog } from '@components/UI/Dialog'
import { ExploreResource } from '@models/explore'
import {
  ExternalLinkIcon,
  StarIcon as StarOutlineIcon,
  XIcon
} from '@heroicons/react/outline'

interface ExploreDialogProps {
  resource?: ExploreResource
  handleLiked?: (resource: ExploreResource) => void
  handleClose: () => void
}

export function ExploreDialog(props: ExploreDialogProps) {
  const { resource } = props
  const subject = `Check out this link: ${resource?.title}`
  const body = `I found this link on https://www.majordomo.com. ${resource?.body}. ${resource?.url}`
  const tags = [
    'country',
    'state',
    'county',
    'city',
    'permits',
    'mapping',
    'utilites'
  ]

  return (
    <Dialog
      isOpen={Boolean(resource)}
      hideHeader={true}
      handleClose={props.handleClose}
      width="xl"
    >
      {resource && (
        <div className="relative w-full">
          <div
            className="absolute top-4 right-4 p-2 border border-gray-100 bg-white rounded-full text-black shadow-md transform-gpu transition hover:scale-105 hover:shadow-lg cursor-pointer"
            onClick={props.handleClose}
            data-testid="closeDialog"
          >
            <XIcon width={24} height={24} />
          </div>

          <img
            src={resource.image}
            className="w-full h-72 object-cover rounded-t"
          />

          <div className="md:px-8 px-4 pt-8 pb-4">
            <div className="grid grid-cols-5 mb-4">
              <div className="col-span-4">
                <h4 className="font-black text-xl leading-tight mb-2">
                  {resource.title}
                </h4>
                <div className="flex space-x-2">
                  {tags.map(tag => {
                    if (resource.tags?.[tag]) {
                      return (
                        <div className="px-2 py-1 rounded bg-blue-200 font-bold text-brand text-sm">
                          <p className="capitalize">{tag}</p>
                        </div>
                      )
                    }
                  })}
                </div>
              </div>

              {resource.tags && (
                <div className="col-span-1">
                  {resource.tags?.liked ? (
                    <StarIcon
                      id="starLiked"
                      width={32}
                      height={32}
                      className="ml-auto transition transform hover:scale-110 cursor-pointer"
                      onClick={async () => props.handleLiked?.(resource)}
                    />
                  ) : (
                    <StarOutlineIcon
                      id="star"
                      width={32}
                      height={32}
                      className="ml-auto transition transform hover:scale-110 cursor-pointer"
                      onClick={async () => props.handleLiked?.(resource)}
                    />
                  )}
                </div>
              )}
            </div>
            <p>{resource.body}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 items-center md:px-8 px-4 pb-8">
            <a
              href={`mailto:?subject=${subject}&body=${body}`}
              className="w-full"
            >
              <button className="flex items-center justify-center rounded bg-blue-500 font-bold text-white w-full px-4 py-2">
                Share this link{' '}
                <MailIcon height={16} width={16} className="ml-2" />
              </button>
            </a>

            <a
              href={resource.url}
              className="w-full"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="flex items-center justify-center rounded bg-gold-400 font-bold text-white w-full px-4 py-2">
                View Resource{' '}
                <ExternalLinkIcon height={16} width={16} className="ml-2" />
              </button>
            </a>
          </div>
        </div>
      )}
    </Dialog>
  )
}
